import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import '@styles/client-admin/professional-official-management.scss'
import CompactOfficialForm from '@components/client-admin/CompactOfficialForm'
import { useToast, ToastContainer } from '@components/common/Toast'
import api, { getStorageUrl } from '../../services/apiService'

// All Indian States
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
]

const ProfessionalOfficialManagement = () => {
  const navigate = useNavigate()
  const { toasts, showToast, removeToast, success, error, warning, info } = useToast()
  const [officials, setOfficials] = useState([])
  const [filteredOfficials, setFilteredOfficials] = useState([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState('table') // 'grid' or 'table'
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterType, setFilterType] = useState('all')
  const [showForm, setShowForm] = useState(false)
  const [editingOfficial, setEditingOfficial] = useState(null)
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, blocked: 0 })
  const [selectedOfficials, setSelectedOfficials] = useState([])

  // State-wise view states
  const [currentView, setCurrentView] = useState('states') // 'states', 'officials', 'detail'
  const [stateData, setStateData] = useState([])
  const [selectedState, setSelectedState] = useState(null)
  const [selectedOfficial, setSelectedOfficial] = useState(null)

  // Format date to "13 Jun 2013" format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    const day = date.getDate()
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const month = monthNames[date.getMonth()]
    const year = date.getFullYear()
    return `${day} ${month} ${year}`
  }

  useEffect(() => {
    loadOfficials()
  }, [])

  useEffect(() => {
    filterOfficials()
  }, [officials, searchTerm, filterStatus, filterType])

  const loadOfficials = async () => {
    try {
      setLoading(true)
      const response = await api.get('/client-admin/officials')
      const result = response.data

      if (result.success) {
        const officialsData = result.data || []
        setOfficials(officialsData)
        setStats(result.stats || { total: 0, active: 0, pending: 0, blocked: 0 })

        // Calculate state-wise data
        calculateStateData(officialsData)
      } else {
        console.error('Failed to load officials:', result.message)
      }
    } catch (err) {
      console.error('Error loading officials:', err)
    } finally {
      setLoading(false)
    }
  }

  const filterOfficials = () => {
    let filtered = [...officials]

    if (searchTerm) {
      filtered = filtered.filter(official =>
        official.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        official.regId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        official.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        official.contact?.includes(searchTerm)
      )
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(official =>
        official.status?.toLowerCase() === filterStatus.toLowerCase()
      )
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(official =>
        official.officialType?.toLowerCase() === filterType.toLowerCase()
      )
    }

    setFilteredOfficials(filtered)
  }

  // Calculate state-wise data
  const calculateStateData = (officialsData) => {
    const stateMap = {}

    // Don't initialize all states - only count states that have officials
    officialsData.forEach(official => {
      const state = official.state || 'Unknown'
      if (!stateMap[state]) {
        stateMap[state] = {
          name: state,
          count: 0,
          officials: []
        }
      }
      stateMap[state].count++
      stateMap[state].officials.push(official)
    })

    // Sort by state name and only show states with officials
    const stats = Object.values(stateMap)
      .filter(state => state.count > 0)
      .sort((a, b) => a.name.localeCompare(b.name))

    setStateData(stats)
  }

  // State view navigation
  const handleStateClick = (state) => {
    if (state.count === 0) {
      info(`No officials found in ${state.name}`)
      return
    }
    setSelectedState(state)
    setCurrentView('officials')
  }

  const handleOfficialClick = (official) => {
    setSelectedOfficial(official)
    setCurrentView('detail')
  }

  const handleBackToStates = () => {
    setCurrentView('states')
    setSelectedState(null)
    setSelectedOfficial(null)
  }

  const handleBackToOfficials = () => {
    setCurrentView('officials')
    setSelectedOfficial(null)
  }

  const handleAddNew = () => {
    setEditingOfficial(null)
    setShowForm(true)
  }

  const handleEdit = (official) => {
    setEditingOfficial(official)
    setShowForm(true)
  }

  const handleDelete = async (officialId) => {
    if (!confirm('Are you sure you want to delete this official?')) return

    try {
      const response = await api.delete(`/client-admin/officials/${officialId}`)
      const result = response.data

      if (result.success) {
        success('Official deleted successfully')
        await loadOfficials()
      } else {
        error(result.message || 'Failed to delete official')
      }
    } catch (err) {
      console.error('Error deleting official:', err)
      error('Error deleting official')
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditingOfficial(null)
  }

  const handleFormSuccess = (message) => {
    success(message || 'Official saved successfully')
    loadOfficials()
    handleFormClose()
  }

  const handleSelectOfficial = (officialId) => {
    setSelectedOfficials(prev =>
      prev.includes(officialId)
        ? prev.filter(id => id !== officialId)
        : [...prev, officialId]
    )
  }

  const handleSelectAll = () => {
    if (selectedOfficials.length === filteredOfficials.length) {
      setSelectedOfficials([])
    } else {
      setSelectedOfficials(filteredOfficials.map(o => o.id))
    }
  }

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'badge-success'
      case 'pending': return 'badge-warning'
      case 'blocked': return 'badge-danger'
      default: return 'badge-secondary'
    }
  }

  const getOfficialTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'umpire': return 'fa-gavel'
      case 'scorer': return 'fa-calculator'
      case 'coach': return 'fa-chalkboard-teacher'
      default: return 'fa-user-tie'
    }
  }

  if (loading) {
    return (
      <div className="professional-official-management">
        <ToastContainer toasts={toasts} removeToast={removeToast} />
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading officials...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="professional-official-management">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <div className="title-section">
            <h1><i className="fas fa-user-tie"></i> Officials Management</h1>
            <p className="subtitle">Manage and organize baseball officials</p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button className="btn-primary" onClick={handleAddNew}>
              <i className="fas fa-plus"></i>
              Add New Official
            </button>
            <button
              onClick={() => navigate('/admin/dashboard')}
              style={{
                background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 100%)',
                border: 'none',
                color: 'white',
                borderRadius: '8px',
                padding: '10px 20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '0.85rem',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(192, 15, 21, 0.4)'
              }}
            >
              <i className="fas fa-arrow-left"></i> Back
            </button>
          </div>
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      {currentView !== 'states' && (
        <div className="breadcrumb-nav">
          <button
            className={currentView === 'states' ? 'active' : ''}
            onClick={handleBackToStates}
          >
            <i className="fas fa-users"></i>
            All Officials
          </button>
          {currentView !== 'states' && (
            <>
              <i className="fas fa-chevron-right"></i>
              <button
                className={currentView === 'officials' ? 'active' : ''}
                onClick={handleBackToOfficials}
              >
                <i className="fas fa-user-tie"></i>
                {selectedState?.name} ({selectedState?.count})
              </button>
            </>
          )}
          {currentView === 'detail' && (
            <>
              <i className="fas fa-chevron-right"></i>
              <button className="active">
                <i className="fas fa-user"></i>
                {selectedOfficial?.fullName}
              </button>
            </>
          )}
        </div>
      )}

      {/* View 1: All Officials Table */}
      {currentView === 'states' && (
        <div className="states-table-section">
          <div className="section-header">
            <h2>All Officials</h2>
            <div className="total-badge">
              <i className="fas fa-user-tie"></i>
              Total: {stats.total} Officials
            </div>
          </div>
          <div className="table-wrapper">
            <table className="officials-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Reg ID</th>
                  <th>Official Name</th>
                  <th>Date of Birth</th>
                  <th>Age</th>
                  <th>Gender</th>
                  <th>Position</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {officials.length > 0 ? (
                  officials.map((official, index) => (
                    <tr key={official.id}>
                      <td>{index + 1}</td>
                      <td className="reg-id">{official.regId}</td>
                      <td className="official-name">
                        <i className="fas fa-user"></i>
                        {official.fullName}
                      </td>
                      <td>{formatDate(official.dob)}</td>
                      <td>{official.age || 'N/A'}</td>
                      <td>{official.gender || 'N/A'}</td>
                      <td>{official.position}</td>
                      <td>
                        <span className="category-badge">
                          {official.category || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge-status badge-${official.status?.toLowerCase()}`}>
                          {official.status}
                        </span>
                      </td>
                      <td className="actions">
                        <button
                          className="btn-view-simple"
                          onClick={() => handleOfficialClick(official)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="no-data-row">
                      <i className="fas fa-info-circle"></i>
                      No officials added yet. Click "Add New Official" to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View 2: Officials List for Selected State */}
      {currentView === 'officials' && selectedState && (
        <div className="state-officials-section">
          <div className="section-header">
            <h2>Officials from {selectedState.name}</h2>
            <div className="total-badge">
              <i className="fas fa-user-tie"></i>
              {selectedState.count} {selectedState.count === 1 ? 'Official' : 'Officials'}
            </div>
          </div>
          <div className="table-wrapper">
            <table className="officials-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Reg ID</th>
                  <th>Full Name</th>
                  <th>DOB</th>
                  <th>Age</th>
                  <th>Gender</th>
                  <th>Official Type</th>
                  <th>Category</th>
                  <th>Contact</th>
                  <th>Email</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedState.officials.map((official, index) => (
                  <tr key={official.id}>
                    <td>{index + 1}</td>
                    <td className="reg-id">{official.regId}</td>
                    <td className="official-name">
                      <div className="official-avatar-small">
                        {official.image ? (
                          <img
                            src={getStorageUrl(official.image)}
                            alt={official.fullName}
                          />
                        ) : (
                          <i className="fas fa-user-circle"></i>
                        )}
                      </div>
                      {official.fullName}
                    </td>
                    <td>{formatDate(official.dob)}</td>
                    <td>{official.age || 'N/A'}</td>
                    <td>{official.gender || 'N/A'}</td>
                    <td>{official.officialType}</td>
                    <td>
                      <span className="category-badge">
                        {official.category || 'N/A'}
                      </span>
                    </td>
                    <td>{official.contact}</td>
                    <td>{official.email}</td>
                    <td className="actions">
                      <button
                        className="btn-detail"
                        onClick={() => handleOfficialClick(official)}
                      >
                        <i className="fas fa-info-circle"></i>
                        View Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View 3: Official Detail */}
      {currentView === 'detail' && selectedOfficial && (
        <div className="official-detail-section">
          <div className="detail-header-card">
            <div className="official-avatar-large">
              {selectedOfficial.image ? (
                <img
                  src={getStorageUrl(selectedOfficial.image)}
                  alt={selectedOfficial.fullName}
                />
              ) : (
                <i className="fas fa-user-circle"></i>
              )}
            </div>
            <div className="official-main-info">
              <h2>{selectedOfficial.fullName}</h2>
              <p className="reg-id-large">Registration ID: {selectedOfficial.regId}</p>
              <p className="state-info"><i className="fas fa-map-marker-alt"></i> {selectedOfficial.state}</p>
            </div>
            <div className="detail-actions">
              <button className="btn-edit-official" onClick={() => handleEdit(selectedOfficial)}>
                <i className="fas fa-edit"></i>
                Edit Official's Detail
              </button>
              <button className="btn-delete-official" onClick={() => handleDelete(selectedOfficial.id)}>
                <i className="fas fa-trash-alt"></i>
                Delete Official
              </button>
            </div>
          </div>

          <div className="detail-sections">
            <div className="detail-section">
              <h3><i className="fas fa-user"></i> Personal Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Full Name</label>
                  <div className="value">{selectedOfficial.fullName}</div>
                </div>
                <div className="detail-item">
                  <label>Father's Name</label>
                  <div className="value">{selectedOfficial.fatherName}</div>
                </div>
                <div className="detail-item">
                  <label>Date of Birth</label>
                  <div className="value">{selectedOfficial.dob}</div>
                </div>
                <div className="detail-item">
                  <label>Age</label>
                  <div className="value">{selectedOfficial.age} years</div>
                </div>
                <div className="detail-item">
                  <label>Gender</label>
                  <div className="value">{selectedOfficial.gender}</div>
                </div>
                <div className="detail-item">
                  <label>Marital Status</label>
                  <div className="value">{selectedOfficial.maritalStatus}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3><i className="fas fa-id-badge"></i> Official Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Official Type</label>
                  <div className="value">{selectedOfficial.officialType}</div>
                </div>
                {/* Commented out for future use */}
                {/* <div className="detail-item">
                  <label>Qualification</label>
                  <div className="value">{selectedOfficial.qualification}</div>
                </div>
                <div className="detail-item">
                  <label>Experience (Years)</label>
                  <div className="value">{selectedOfficial.experience}</div>
                </div> */}
                <div className="detail-item">
                  <label>Status</label>
                  <div className="value">{selectedOfficial.status}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3><i className="fas fa-map-marked-alt"></i> Address Information</h3>
              <div className="detail-grid">
                <div className="detail-item full-width">
                  <label>Address</label>
                  <div className="value">{selectedOfficial.address}</div>
                </div>
                {/* Commented out for future use */}
                {/* <div className="detail-item">
                  <label>City</label>
                  <div className="value">{selectedOfficial.city}</div>
                </div> */}
                <div className="detail-item">
                  <label>State</label>
                  <div className="value">{selectedOfficial.state}</div>
                </div>
                <div className="detail-item">
                  <label>Pin Code</label>
                  <div className="value">{selectedOfficial.pinCode}</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3><i className="fas fa-phone"></i> Contact Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Email</label>
                  <div className="value">{selectedOfficial.email}</div>
                </div>
                <div className="detail-item">
                  <label>Contact Number</label>
                  <div className="value">{selectedOfficial.contact}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Old Controls Section - Hidden */}
      {false && (
        <>
      <div className="controls-section">
        <div className="search-filter-group">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search officials..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="blocked">Blocked</option>
          </select>

          <select
            className="filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="state_official">State Official</option>
            <option value="tournament_official">Tournament Official</option>
            <option value="other_official">Other Official</option>
          </select>
        </div>

        <div className="view-controls">
          <button
            className={`btn-view ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            <i className="fas fa-th"></i>
          </button>
          <button
            className={`btn-view ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            <i className="fas fa-list"></i>
          </button>
        </div>
      </div>

      {/* Officials Display */}
      {filteredOfficials.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-user-tie"></i>
          <h3>No Officials Found</h3>
          <p>Start by adding your first official or adjust your filters</p>
          <button className="btn-primary" onClick={handleAddNew}>
            <i className="fas fa-plus"></i>
            Add First Official
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="officials-grid">
          {filteredOfficials.map((official) => (
            <div key={official.id} className="official-card">
              <div className="card-header">
                <div className="official-avatar">
                  {official.image ? (
                    <img
                      src={getStorageUrl(official.image)}
                      alt={official.fullName}
                    />
                  ) : (
                    <i className={`fas ${getOfficialTypeIcon(official.officialType)}`}></i>
                  )}
                </div>
                <span className={`status-badge ${getStatusBadgeClass(official.status)}`}>
                  {official.status}
                </span>
              </div>

              <div className="card-body">
                <h3>{official.fullName}</h3>
                <p className="reg-id">
                  <i className="fas fa-id-card"></i>
                  {official.regId}
                </p>

                <div className="official-info">
                  <div className="info-item">
                    <i className="fas fa-briefcase"></i>
                    <span>{official.officialType}</span>
                  </div>
                  <div className="info-item">
                    <i className="fas fa-user-tag"></i>
                    <span>{official.position}</span>
                  </div>
                  <div className="info-item">
                    <i className="fas fa-phone"></i>
                    <span>{official.contact}</span>
                  </div>
                  <div className="info-item">
                    <i className="fas fa-envelope"></i>
                    <span>{official.email}</span>
                  </div>
                  <div className="info-item">
                    <i className="fas fa-map-marker-alt"></i>
                    <span>{official.state}</span>
                  </div>
                  <div className="info-item">
                    <i className="fas fa-calendar"></i>
                    <span>Age: {official.age || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="card-footer">
                <button className="btn-action btn-edit" onClick={() => handleEdit(official)}>
                  <i className="fas fa-edit"></i>
                  Edit
                </button>
                <button className="btn-action btn-delete" onClick={() => handleDelete(official.id)}>
                  <i className="fas fa-trash"></i>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="officials-table-container">
          <table className="officials-table professional-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedOfficials.length === filteredOfficials.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Official</th>
                <th>DOB</th>
                <th>Age</th>
                <th>Gender</th>
                <th>Type</th>
                <th>Category</th>
                <th>Contact</th>
                <th>State</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOfficials.map((official) => (
                <tr key={official.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedOfficials.includes(official.id)}
                      onChange={() => handleSelectOfficial(official.id)}
                    />
                  </td>
                  <td>
                    <div className="table-official-info">
                      <div className="official-avatar-small">
                        {official.image ? (
                          <img
                            src={getStorageUrl(official.image)}
                            alt={official.fullName}
                          />
                        ) : (
                          <i className={`fas ${getOfficialTypeIcon(official.officialType)}`}></i>
                        )}
                      </div>
                      <div>
                        <strong>{official.fullName}</strong>
                        <div className="table-reg-id">{official.regId}</div>
                      </div>
                    </div>
                  </td>
                  <td>{formatDate(official.dob)}</td>
                  <td>{official.age || 'N/A'}</td>
                  <td>{official.gender || 'N/A'}</td>
                  <td>
                    <span className="type-badge">
                      {official.officialType === 'state_official' ? 'State Official' :
                       official.officialType === 'tournament_official' ? 'Tournament Official' :
                       'Other Official'}
                    </span>
                  </td>
                  <td>
                    <span className="category-badge">
                      {official.category || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <div>
                      <div>{official.contact}</div>
                      {official.email && <div className="table-email">{official.email}</div>}
                    </div>
                  </td>
                  <td>{official.state}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(official.status)}`}>
                      <i className={official.status === 'Active' ? 'fas fa-check-circle' : 'fas fa-clock'}></i>
                      {official.status}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="action-btn edit"
                        onClick={() => handleEdit(official)}
                        title="Edit"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => handleDelete(official.id)}
                        title="Delete"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
        </>
      )}

      {/* Compact Form Modal */}
      {showForm && (
        <CompactOfficialForm
          official={editingOfficial}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
          showToast={{ success, error, warning, info }}
        />
      )}
    </div>
  )
}

export default ProfessionalOfficialManagement